import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { useMutation } from '@tanstack/react-query';
import NumberInput from '../../components/form/NumberInput';

/**
 * Modal for bulk-generating physical CaseUnit records for a given ItemCase.
 *
 * Props:
 *   caseId     {number}   – the ItemCase pk
 *   caseName   {string}   – displayed in the header
 *   stockOptions {Array<{value, label}>} – warehouses the new units can be placed in (optional)
 *   onClose    {function} – called when the modal should close
 *   onCreated  {function} – called with the array of created units after success
 */
const BulkGenerateCaseUnitsModal = ({ caseId, caseName, defaultCount = 1, defaultPrefix = '', stockOptions = [], onClose, onCreated }) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(Math.max(1, defaultCount));
  const [prefix, setPrefix] = useState(defaultPrefix);
  const [startIndex, setStartIndex] = useState(1);
  const [stock, setStock] = useState('');
  const [error, setError] = useState(null);

  const submitMutation = useMutation({
    mutationFn: () => api.post('warehouses/api/case-units/bulk_create/', {
      case: caseId, count, prefix, start_index: startIndex, stock: stock || null,
    }),
    onSuccess: (result) => { onCreated(result); onClose(); },
    onError: (err) => { setError(err?.detail || err?.message || t('warehouses.bulkGenerate.error')); },
  });
  const submitting = submitMutation.isPending;

  const previewSerials = () => {
    if (!prefix) return null;
    const items = [];
    for (let i = 0; i < Math.min(count, 5); i++) {
      items.push(`${prefix}${startIndex + i}`);
    }
    if (count > 5) items.push('…');
    return items.join(', ');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    submitMutation.mutate();
  };

  return (
    <div className="modal modal-blur fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{t('warehouses.bulkGenerate.title')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={submitting} />
            </div>

            <div className="modal-body">
              <p className="text-muted mb-3">
                {t('warehouses.bulkGenerate.item')}: <strong>{caseName}</strong>
                {defaultCount > 0 && (
                  <span className="ms-2 badge bg-blue-lt">
                    {t('warehouses.bulkGenerate.missingBadge_other', { count: defaultCount })}
                  </span>
                )}
              </p>

              {error && <div className="alert alert-danger">{error}</div>}

              <div className="mb-3">
                <label className="form-label">{t('warehouses.bulkGenerate.count')} <span className="text-danger">*</span></label>
                <NumberInput
                  min={1}
                  max={500}
                  step="any"
                  value={count}
                  onChange={setCount}
                  required
                />
                <small className="text-muted">{t('warehouses.bulkGenerate.countMax')}</small>
              </div>

              <div className="mb-3">
                <label className="form-label">{t('warehouses.bulkGenerate.prefix')}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('warehouses.bulkGenerate.prefixPlaceholder')}
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  maxLength={50}
                />
                <small className="text-muted">{t('warehouses.bulkGenerate.prefixHint')}</small>
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="bulk-case-units-stock">{t('warehouses.cases.stock')}</label>
                <select
                  id="bulk-case-units-stock"
                  className="form-select"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                >
                  <option value="">{t('warehouses.cases.noStock')}</option>
                  {stockOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <small className="text-muted">{t('warehouses.cases.bulkStockHint')}</small>
              </div>

              {prefix && (
                <div className="mb-3">
                  <label className="form-label">{t('warehouses.bulkGenerate.startIndex')}</label>
                  <NumberInput
                    min={0}
                    step="any"
                    value={startIndex}
                    onChange={setStartIndex}
                  />
                </div>
              )}

              {prefix && (
                <div className="alert alert-info py-2">
                  <strong>{t('warehouses.bulkGenerate.preview')}:</strong> {previewSerials()}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                {t('warehouses.bulkGenerate.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? t('warehouses.bulkGenerate.submitting') : t('warehouses.bulkGenerate.submit', { count })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkGenerateCaseUnitsModal;
